import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  HostListener,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RouterModule, RouterOutlet } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { Subject, takeUntil, filter } from 'rxjs';
import { AppIconComponent } from '../lib/ui';
import { ProfileSelectorComponent } from '../shared/components/profile-selector/profile-selector.component';
import { NavigationMenuModernComponent } from '../shared/components/layout/navigation-menu-modern.component';
import { WhatsNewBannerComponent } from '../shared/components/whats-new-banner/whats-new-banner.component';
import { GlobalScannerComponent } from '../shared/components/global-scanner/global-scanner.component';
import { ProfileViewService, UserRole } from '../core/profile-view.service';
import { PWAInstallBannerComponent } from '../shared/components/pwa/pwa-install-banner.component';
import { NotificationCenterComponent } from '../shared/components/notifications/notification-center.component';
import { CameraCaptureComponent } from '../shared/components/camera/camera-capture.component';
import { PWAService } from '../core/pwa.service';
import { NotificationService } from '../core/notification.service';
import { ShareButtonComponent } from '../shared/components/share/share-button.component';
import { NotificationContainerComponent } from '../shared/components/notification-container/notification-container.component';

export interface MenuItem {
  title: string;
  icon: string;
  route: string;
  roles: string[];
  category: string;
  isNew?: boolean;
  newBadge?: string;
  isUpdated?: boolean;
  description?: string;
}

export interface MenuCategory {
  id: string;
  name: string;
  icon: string;
}

/**
 * Main layout component using Angular 20 patterns
 * Handles navigation, authentication, and permission-based menu rendering
 */
@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    RouterOutlet,
    AppIconComponent,
    ProfileSelectorComponent,
    NavigationMenuModernComponent,
    WhatsNewBannerComponent,
    GlobalScannerComponent,
    PWAInstallBannerComponent,
    NotificationCenterComponent,
    CameraCaptureComponent,
    ShareButtonComponent,
    NotificationContainerComponent,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private profileViewService = inject(ProfileViewService);
  private destroy$ = new Subject<void>();

  // Signals for reactive state management
  username = signal<string>('');
  userRoles = signal<string[]>([]);
  isMenuOpen = signal<boolean>(true);
  isUserMenuOpen = signal<boolean>(false);
  currentViewRole = signal<string>('');

  @ViewChild('userMenuButton') userMenuButton!: ElementRef;
  isViewModeActive = signal<boolean>(false);
  isAuthenticated = signal<boolean>(false);
  isPublicRoute = signal<boolean>(false);

  // Reactive signals for profile view
  activeProfileConfig = signal<any>(null);

  // Permission signals
  isEstokaiOwner = signal<boolean>(false);
  canAccessProducts = signal<boolean>(false);
  canAccessCategories = signal<boolean>(false);
  canAccessBrands = signal<boolean>(false);
  canAccessSales = signal<boolean>(false);
  canAccessSuppliers = signal<boolean>(false);
  canAccessReports = signal<boolean>(false);
  canAccessUsers = signal<boolean>(false);
  canAccessIntegrations = signal<boolean>(false);
  canSelectStore = signal<boolean>(false);

  // Menu items configuration with computed filtered items
  menuItems: MenuItem[] = [
    // === DASHBOARD (SEM CATEGORIA) ===
    {
      title: 'Dashboard',
      icon: 'dashboard',
      route: '/dashboard',
      roles: ['owner', 'financial_manager', 'sales_user', 'partner'],
      category: '', // Dashboard sem categoria
      isUpdated: true,
      description: 'Dashboard visual interativo com KPIs em tempo real',
    },

    // === ESTOQUE ===
    {
      title: 'Produtos',
      icon: 'inventory_2',
      route: '/products',
      roles: ['owner', 'financial_manager', 'stock_manager', 'sales_user'],
      category: 'inventory',
      isUpdated: true,
    },
    {
      title: '📱 Scanner QR/Barcode',
      icon: 'qr_code_scanner',
      route: '/scanner',
      roles: ['owner', 'financial_manager', 'stock_manager', 'sales_user'],
      category: 'inventory',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Scanner inteligente com IA, histórico e busca por foto',
    },
    {
      title: 'Localizações do Estoque',
      icon: 'location_on',
      route: '/inventory-locations',
      roles: ['owner', 'financial_manager', 'stock_manager'],
      category: 'inventory',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Gestão hierárquica avançada de localizações',
    },
    {
      title: 'Categorias',
      icon: 'category',
      route: '/categories',
      roles: ['owner', 'financial_manager', 'stock_manager'],
      category: 'inventory',
    },
    {
      title: 'Marcas',
      icon: 'branding_watermark',
      route: '/brands',
      roles: ['owner', 'financial_manager', 'stock_manager'],
      category: 'inventory',
    },

    // === VENDAS ===
    {
      title: '🚀 PDV Inovador',
      icon: 'point_of_sale',
      route: '/pdv',
      roles: ['owner', 'financial_manager', 'sales_user'],
      category: 'sales',
      isNew: true,
      newBadge: 'NOVO',
      description:
        'PDV revolucionário com IA, comandos de voz, gamificação e PIX avançado',
    },
    {
      title: 'Vendas',
      icon: 'shopping_cart',
      route: '/sales',
      roles: ['owner', 'financial_manager', 'sales_user'],
      category: 'sales',
      isUpdated: true,
    },
    {
      title: 'Clientes',
      icon: 'people_outline',
      route: '/customers',
      roles: ['owner', 'financial_manager', 'sales_user', 'stock_manager'],
      category: 'sales',
      description:
        'Gestão completa de clientes PF/PJ com CRM integrado, WhatsApp e insights',
    },

    // === COMPRAS ===
    {
      title: 'Fornecedores',
      icon: 'local_shipping',
      route: '/suppliers',
      roles: ['owner', 'financial_manager', 'stock_manager'],
      category: 'purchases',
      isUpdated: true,
      description: 'Gestão modernizada de fornecedores com infinite scroll',
    },
    {
      title: 'Pedidos de Compra',
      icon: 'shopping_bag',
      route: '/purchase-orders',
      roles: ['owner', 'financial_manager', 'stock_manager'],
      category: 'purchases',
      isNew: true,
      newBadge: 'NOVO',
      description:
        'Controle completo de pedidos e compras com automação inteligente',
    },

    // === FINANCEIRO ===
    {
      title: 'Contas a Receber',
      icon: 'attach_money',
      route: '/accounts-receivable',
      roles: ['owner', 'financial_manager', 'partner'],
      category: 'financial',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Gestão completa de recebíveis e cobranças com automação',
    },
    {
      title: 'Contas a Pagar',
      icon: 'money_off',
      route: '/accounts-payable',
      roles: ['owner', 'financial_manager'],
      category: 'financial',
      isNew: true,
      newBadge: 'NOVO',
      description:
        'Gestão completa de contas a pagar com aprovação em lote e automações',
    },
    {
      title: 'Relatórios Contas a Pagar',
      icon: 'analytics',
      route: '/accounts-payable-reports',
      roles: ['owner', 'financial_manager'],
      category: 'financial',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Analytics e relatórios avançados de contas a pagar',
    },
    {
      title: 'Fluxo de Caixa',
      icon: 'account_balance_wallet',
      route: '/cash-flow',
      roles: ['owner', 'financial_manager', 'partner'],
      category: 'financial',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Análise de fluxo de caixa e projeções inteligentes',
    },

    // === RELATÓRIOS ===
    {
      title: 'Relatórios',
      icon: 'assessment',
      route: '/reports',
      roles: ['owner', 'financial_manager', 'partner'],
      category: 'reports',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Central de relatórios e análises do negócio',
    },
    {
      title: 'Dashboard de Relatórios',
      icon: 'dashboard',
      route: '/reports/dashboard',
      roles: ['owner', 'financial_manager', 'partner'],
      category: 'reports',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Visão geral completa com KPIs e métricas essenciais',
    },
    {
      title: 'Relatórios de Vendas',
      icon: 'trending_up',
      route: '/reports/sales',
      roles: ['owner', 'financial_manager', 'partner'],
      category: 'reports',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Análise detalhada de performance de vendas e conversões',
    },
    {
      title: 'Relatórios de Estoque',
      icon: 'assessment',
      route: '/reports/stock',
      roles: ['owner', 'financial_manager', 'partner'],
      category: 'reports',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Controle de inventário com alertas inteligentes',
    },
    {
      title: 'Top Produtos',
      icon: 'star',
      route: '/reports/top-products',
      roles: ['owner', 'financial_manager', 'partner'],
      category: 'reports',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Ranking de produtos mais vendidos e análise de performance',
    },
    {
      title: 'Resumo Financeiro',
      icon: 'pie_chart',
      route: '/reports/financial',
      roles: ['owner', 'financial_manager', 'partner'],
      category: 'reports',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Análise completa da saúde financeira com insights',
    },

    // === CONFIGURAÇÕES ===
    {
      title: '🎯 Onboarding Gamificado',
      icon: 'school',
      route: '/onboarding',
      roles: ['owner', 'financial_manager', 'sales_user'],
      category: 'settings',
      isNew: true,
      newBadge: 'NOVO',
      description: 'Tutorial interativo gamificado com badges e achievements',
    },
    {
      title: 'Usuários',
      icon: 'people',
      route: '/users',
      roles: ['owner'],
      category: 'settings',
      description: 'Gestão de usuários e permissões do sistema',
    },
    {
      title: 'Integrações',
      icon: 'hub',
      route: '/integrations',
      roles: ['owner'],
      category: 'settings',
      isNew: true,
      description: 'Integração com sistemas externos e APIs',
    },
    {
      title: 'Configurações',
      icon: 'cog-6-tooth',
      route: '/settings',
      roles: ['owner', 'manager', 'dono', 'gerente', 'ESTOKAI_OWNER'],
      category: 'settings',
      description: 'Configurações gerais e parametrizações do sistema',
    },
  ];

  categories: MenuCategory[] = [
    { id: 'inventory', name: '📦 Estoque', icon: 'inventory_2' },
    { id: 'sales', name: '💰 Vendas', icon: 'point_of_sale' },
    { id: 'purchases', name: '🛒 Compras', icon: 'local_shipping' },
    { id: 'financial', name: '💳 Financeiro', icon: 'account_balance' },
    { id: 'reports', name: '📈 Relatórios', icon: 'analytics' },
    { id: 'settings', name: '⚙️ Sistema', icon: 'settings' },
  ];

  // Computed properties for filtered menu items
  filteredMenuItems = computed(() => {
    const roles = this.userRoles();

    // ESTOKAI_OWNER vê todos os itens
    if (roles.includes('ESTOKAI_OWNER')) {
      return this.menuItems;
    }

    return this.menuItems.filter((item) => this.hasAccessToItem(item, roles));
  });

  categoriesWithItems = computed(() => {
    const filteredItems = this.filteredMenuItems();
    return this.categories.filter((category) =>
      filteredItems.some((item) => item.category === category.id)
    );
  });

  constructor() {}

  async ngOnInit() {
    console.log('🚀 Layout initialization started');

    // Check if on public route
    await this.checkAuthentication();

    // Verificar se é ESTOKAI_OWNER imediatamente
    const userRoles = await this.authService.getUserRoles();
    const isEstokaiOwner = userRoles.includes('ESTOKAI_OWNER');

    console.log('🔑 Initial user roles check:', userRoles);
    console.log('👑 Is ESTOKAI_OWNER:', isEstokaiOwner);

    if (isEstokaiOwner) {
      console.log(
        '✅ ESTOKAI_OWNER detected - updating permissions immediately'
      );
      this.isEstokaiOwner.set(true);
      this.userRoles.set(['ESTOKAI_OWNER']);
      this.currentViewRole.set('ESTOKAI_OWNER');
    }

    // Observe route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(async () => {
        await this.checkAuthentication();
      });

    // Listen for profile view changes
    this.profileViewService
      .getCurrentViewProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (profileRole: UserRole) => {
        this.currentViewRole.set(profileRole);
        this.activeProfileConfig.set(
          this.profileViewService.getCurrentProfileConfig()
        );
        await this.updatePermissions();
        console.log(
          'Profile changed:',
          profileRole,
          this.activeProfileConfig()
        );
      });

    // Listen for view mode changes
    this.profileViewService
      .getIsViewModeActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isActive: boolean) => {
        this.isViewModeActive.set(isActive);
      });

    // Listen for profile view change events
    window.addEventListener('profileViewChanged', async (e: any) => {
      console.log('Profile view changed event received:', e.detail);
      await this.updatePermissions();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadUserData() {
    const username = (await this.authService.getUsername()) || 'Usuário Demo';
    const userRoles = await this.authService.getUserRoles();

    // For demo purposes, if no roles, use owner profile
    if (userRoles.length === 0) {
      this.userRoles.set(['owner', 'financial_manager', 'sales_user']);
    } else {
      this.userRoles.set(userRoles);
    }

    // Force owner role for testing financial features
    this.userRoles.set(['owner', 'financial_manager', 'sales_user']);

    this.username.set(username);
  }

  private async updatePermissions() {
    console.log('Updating permissions for role:', this.currentViewRole());

    // Get current profile config from service
    const profileConfig = this.profileViewService.getCurrentProfileConfig();
    this.activeProfileConfig.set(profileConfig);

    // Update current view role
    this.currentViewRole.set(profileConfig.role);

    // Update user roles based on the active profile
    if (profileConfig.role === 'ESTOKAI_OWNER') {
      this.userRoles.set(['ESTOKAI_OWNER']);
    } else if (
      profileConfig.role === 'owner' ||
      profileConfig.role === 'dono'
    ) {
      this.userRoles.set([
        'owner',
        'manager',
        'financial_manager',
        'sales_user',
      ]);
    } else if (
      profileConfig.role === 'financial_manager' ||
      profileConfig.role === 'gerente'
    ) {
      this.userRoles.set(['manager', 'financial_manager', 'sales_user']);
    } else if (
      profileConfig.role === 'sales_user' ||
      profileConfig.role === 'vendedor'
    ) {
      this.userRoles.set(['sales_user']);
    } else if (profileConfig.role === 'stock_manager') {
      this.userRoles.set(['stock_manager', 'sales_user']);
    } else if (profileConfig.role === 'partner') {
      this.userRoles.set(['partner']);
    }

    console.log(
      'Updated userRoles for profile:',
      profileConfig.role,
      this.userRoles()
    );

    // Use profile config to determine permissions
    const availableFeatures = profileConfig?.availableFeatures || [];
    const restrictedFeatures = profileConfig?.restrictedFeatures || [];

    // Update permission signals
    this.isEstokaiOwner.set(profileConfig.role === 'ESTOKAI_OWNER');
    this.canAccessProducts.set(availableFeatures.includes('products'));
    this.canAccessCategories.set(availableFeatures.includes('categories'));
    this.canAccessBrands.set(availableFeatures.includes('brands'));
    this.canAccessSales.set(availableFeatures.includes('sales'));
    this.canAccessSuppliers.set(availableFeatures.includes('suppliers'));
    this.canAccessReports.set(availableFeatures.includes('reports'));
    this.canAccessUsers.set(availableFeatures.includes('users'));
    this.canAccessIntegrations.set(availableFeatures.includes('integrations'));
    this.canSelectStore.set(
      profileConfig.dashboardConfig?.canViewAllStores || false
    );

    console.log('Permissions updated for role:', profileConfig.role, {
      isEstokaiOwner: this.isEstokaiOwner(),
      canAccessProducts: this.canAccessProducts(),
      canAccessCategories: this.canAccessCategories(),
      canAccessBrands: this.canAccessBrands(),
      canAccessSales: this.canAccessSales(),
      canAccessSuppliers: this.canAccessSuppliers(),
      canAccessReports: this.canAccessReports(),
      canAccessUsers: this.canAccessUsers(),
      canAccessIntegrations: this.canAccessIntegrations(),
      canSelectStore: this.canSelectStore(),
    });
  }

  private async checkAuthentication() {
    const currentUrl = this.router.url;
    const publicRoutes = ['/home', '/login', '/register', '/landing'];

    this.isPublicRoute.set(
      publicRoutes.some((route) => currentUrl.startsWith(route))
    );

    try {
      const authenticated = await this.authService.isLoggedIn();
      this.isAuthenticated.set(authenticated);

      // If authenticated and not on public route, load user data
      if (authenticated && !this.isPublicRoute()) {
        await this.loadUserData();
        await this.updatePermissions();
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      this.isAuthenticated.set(false);
    }
  }

  private hasAccessToItem(item: MenuItem, roles: string[]): boolean {
    // Debug logging for settings and financial items
    if (
      item.route.includes('settings') ||
      item.route.includes('accounts-payable')
    ) {
      console.log(`🔍 Checking access for ${item.title}:`, {
        route: item.route,
        itemRoles: item.roles,
        userRoles: roles,
        profileConfig: this.activeProfileConfig(),
      });
    }

    // ESTOKAI_OWNER sempre tem acesso a tudo
    if (roles.includes('ESTOKAI_OWNER')) {
      console.log(`✅ ESTOKAI_OWNER access granted for ${item.title}`);
      return true;
    }

    // Owner sempre tem acesso (role moderna)
    if (roles.includes('owner') && item.roles.includes('owner')) {
      console.log(`✅ Owner access granted for ${item.title}`);
      return true;
    }

    // Manager tem acesso a itens que permitem manager
    if (roles.includes('manager') && item.roles.includes('manager')) {
      console.log(`✅ Manager access granted for ${item.title}`);
      return true;
    }

    const profileConfig = this.activeProfileConfig();
    if (!profileConfig) {
      // Fallback direto para role-based check quando não há profile config
      if (roles.length === 0) return true;
      const hasAccess = item.roles.some((role) => roles.includes(role));
      console.log(`🔍 Fallback role check for ${item.title}: ${hasAccess}`);
      return hasAccess;
    }

    // Get available features from the active profile
    const availableFeatures = profileConfig.availableFeatures || [];
    const restrictedFeatures = profileConfig.restrictedFeatures || [];

    // Check if this menu item's route maps to an available feature
    const itemFeature = this.getFeatureFromRoute(item.route);

    // Debug logging
    if (
      item.route.includes('settings') ||
      item.route.includes('accounts-payable')
    ) {
      console.log(`🔍 Feature check for ${item.title}:`, {
        itemFeature,
        availableFeatures,
        restrictedFeatures,
        hasFeature: itemFeature && availableFeatures.includes(itemFeature),
        isRestricted: itemFeature && restrictedFeatures.includes(itemFeature),
      });
    }

    // Configurações sempre acessível para owner/manager (critical system function)
    if (
      item.route === '/settings' &&
      (roles.includes('owner') || roles.includes('manager'))
    ) {
      console.log(`✅ Settings access granted for owner/manager`);
      return true;
    }

    // If it's a known feature, check if it's available in the profile
    if (itemFeature) {
      return (
        availableFeatures.includes(itemFeature) &&
        !restrictedFeatures.includes(itemFeature)
      );
    }

    // Fallback final para role-based permission check
    if (roles.length === 0) return true;
    const hasAccess = item.roles.some((role) => roles.includes(role));
    console.log(`🔍 Final fallback role check for ${item.title}: ${hasAccess}`);
    return hasAccess;
  }

  private getFeatureFromRoute(route: string): string | null {
    // Map routes to feature names
    const routeToFeatureMap: Record<string, string> = {
      '/dashboard': 'dashboard',
      '/products': 'products',
      '/categories': 'categories',
      '/brands': 'brands',
      '/sales': 'sales',
      '/suppliers': 'suppliers',
      '/reports': 'reports',
      '/users': 'users',
      '/integrations': 'integrations',
      '/campaigns': 'campaigns',
      '/pdv': 'sales',
      '/customers': 'sales',
      '/purchase-orders': 'suppliers',
      '/accounts-receivable': 'financial',
      '/accounts-payable': 'financial',
      '/accounts-payable-reports': 'financial',
      '/cash-flow': 'financial',
      '/sales-reports': 'reports',
      '/inventory-reports': 'reports',
      '/financial-reports': 'reports',
      '/reports/dashboard': 'reports',
      '/reports/sales': 'reports',
      '/reports/stock': 'reports',
      '/reports/top-products': 'reports',
      '/reports/financial': 'reports',
      '/settings': 'settings',
      '/inventory-locations': 'products',
    };

    return routeToFeatureMap[route] || null;
  }

  hasAccess(roles: string[]): boolean {
    // For demo purposes, always return true if no roles
    const userRoles = this.userRoles();
    if (userRoles.length === 0) return true;
    return roles.some((role) => userRoles.includes(role));
  }

  getMenuItemsByCategory(categoryId: string): MenuItem[] {
    const filteredItems = this.filteredMenuItems();
    return filteredItems.filter((item) => item.category === categoryId);
  }

  getCategoryName(categoryId: string): string {
    const category = this.categories.find((cat) => cat.id === categoryId);
    return category?.name || '';
  }

  getCategoryIcon(categoryId: string): string {
    const category = this.categories.find((cat) => cat.id === categoryId);
    return category?.icon || 'folder';
  }

  logout() {
    this.authService.logout();
  }

  toggleMenu() {
    this.isMenuOpen.update((value) => !value);
  }

  onMenuItemClick(item: MenuItem) {
    this.router.navigate([item.route]);
  }

  async switchToProfile(role: UserRole) {
    console.log('Switching to profile:', role);
    await this.profileViewService.switchProfileView(role);
  }

  async resetToOriginalView() {
    console.log('Resetting to original view');
    await this.profileViewService.resetToOriginalView();
  }

  async getAvailableProfiles() {
    return await this.profileViewService.getAvailableProfiles();
  }

  getCurrentProfileDisplayName(): string {
    const config = this.activeProfileConfig();
    return config?.displayName || 'Usuário';
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    // Fecha o menu do usuário quando clicar fora dele
    if (
      this.isUserMenuOpen() &&
      this.userMenuButton &&
      !this.userMenuButton.nativeElement.contains(event.target)
    ) {
      this.isUserMenuOpen.set(false);
    }
  }

  toggleUserMenu() {
    this.isUserMenuOpen.update((value) => !value);
  }
}
